import PropTypes from "prop-types";
import { useEffect, useState, useRef, useCallback } from "react";
import { FormControl, ListGroup, Spinner } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { selectIsLoadingStations, selectStations } from "../selectors.js";
import { fetchTrainStationData } from "../thunks.js";

const messages = {
  searchNewStation: {
    id: "modules.TrainStopSignalImages.components.StationList.searchNewStations",
    description: "searchNewStations label",
    defaultMessage: "Search new station...",
  },
  stationError: {
    id: "modules.TrainStopSignalImages.components.StationList.stationError",
    description: "stationError label",
    defaultMessage: "Station not found.",
  },
};

const SearchDropdownForm = ({ setError }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const stations = useSelector(selectStations);
  const isLoading = useSelector(selectIsLoadingStations);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const wrapperRef = useRef(null);

  // debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // fetch data
  const fetchData = useCallback(async () => {
    if (!debouncedSearch) return;

    try {
      setError(null);

      await dispatch(
        fetchTrainStationData({
          search: debouncedSearch,
        }),
      );

      setShowDropdown(true);
    } catch (err) {
      if (err.status === 404) {
        setError(intl.formatMessage(messages.stationError));
      } else {
        setError(err);
      }
    }
  }, [dispatch, debouncedSearch, setError]);

  // trigger fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // close dropdown outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (station) => {
    setSearch(station.name);
    setShowDropdown(false);
  };

  return (
    <div ref={wrapperRef} className="position-relative">
      <FormControl
        type="text"
        placeholder={intl.formatMessage(messages.searchNewStation)}
        value={search || ""}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        onFocus={() => {
          if (stations?.length > 0) {
            setShowDropdown(true);
          }
        }}
      />

      {isLoading && (
        <div className="mt-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {showDropdown && stations?.length > 0 && (
        <ListGroup
          className="position-absolute w-100 overflow-auto mt-1"
          style={{
            zIndex: 10,
          }}
        >
          {stations.map((station) => (
            <ListGroup.Item
              action
              key={station.id.uicCode}
              onClick={() => handleSelect(station)}
            >
              <Link
                to={`/signal-image/add/${station.id.uicCode}`}
                className="text-decoration-none text-reset d-block w-100 py-1"
              >
                {station.names.long}
              </Link>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

SearchDropdownForm.propTypes = {
  setError: PropTypes.func,
};

export default SearchDropdownForm;
